
import React from "react";

import { IconButton, InputAdornment, MenuItem, 
    TextField, Tooltip } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
    
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from 'dayjs';

import ApplicationContext from '../contexts/application';
import { getAlarmPriorities, getSources } from '../libs/apiCalls';

function CustomFilter({ showDates = true, searchOnly = false, filter, onFilter, onClearFilter }) {
    const lastWeek = new Date().getDate() - 7;
    const { settings } = React.useContext(ApplicationContext);

    const [searchString, setSearchString] = React.useState('');
    const [priorityFilter, setPriorityFilter] = React.useState(filter.current.priority);
    const [sourceFilter, setSourceFilter] = React.useState('');

    // Both lists come from the database rather than being written out here. They used to be
    // hard-coded, which was wrong in both directions: a priority added on the Alarms >
    // Priorities screen never appeared, and the source list named six codes while the
    // processors use twelve - so alarms from fire alarm, access control, nurse call, video,
    // SNMP and AAM could not be filtered at all, on any client. It was also wrong per client,
    // offering lighting and RTLS to sites that have neither.
    //
    // AlarmPriority.Name and MessageSource.Name are the values an alarm actually carries, so
    // they are what the filter compares on; MessageSource.Description is the display label.
    const [priorities, setPriorities] = React.useState([]);
    const [sources, setSources] = React.useState([]);

    React.useEffect(() => {
        if (!settings?.token) {
            return;
        }

        // A failure leaves the list at "All" only. The filter is a convenience over search and
        // dates, so degrading it beats blocking the screen it sits on - and handleErrors has
        // already dealt with an expired session by then.
        getAlarmPriorities(settings.token,
            (response) => setPriorities(response.data.error ? [] : JSON.parse(response.data.result)),
            () => setPriorities([]));

        getSources(settings.token,
            (response) => setSources(response.data.error ? [] : JSON.parse(response.data.result)),
            () => setSources([]));
    }, [settings?.token]);

    const handleFilterChange = (event) => {
        filter.current[event.target.name] = event.target.value;

        if (event.target.name === 'searchString') {
            setSearchString(event.target.value);
        }

        setSourceFilter(filter.current.source);

        // do not auto filter when searchstring is changed
        if (event.target.name != 'searchString') {
            handleFilter();
        }
    }

    const handleFilterDateChange = (target, date) => {
        filter.current[target] = date ? dayjs(date.$d).format('YYYY-MM-DD') : '';
        handleFilter();
    };

    const handleFilter = () => {
        onFilter(filter);
    };

    const handleClearFilter = () => {
        filter.current.searchString = '';
        filter.current.fromDate = ''; 
        filter.current.toDate = '';
        filter.current.priority = '';
        filter.current.source = '';
        filter.current.status = 'Active';

        setSourceFilter('');
        // setPriorityFilter('');
        setSearchString('');

        onClearFilter();
    };

    return (
        <>
            <TextField
                variant='outlined'
                name='searchString'
                size='small'
                placeholder='Search for any attribute or value'
                InputProps={{
                    startAdornment: <InputAdornment position='start'><SearchIcon /></InputAdornment>
                }}
                value={searchString}
                onChange={handleFilterChange}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        handleFilter();
                    }
                }}
                sx={{ width: (!searchOnly && showDates)? '40%' : '100%' }} />

            <DatePicker
                name='fromDate'
                slotProps={{
                    textField: {
                        size: 'small',
                        placeholder: 'From Date'
                    }
                }}
                sx={{
                    width: '15%',
                    display: (!searchOnly && showDates) ? 'flex' : 'none'
                }}
                onChange={(date) => handleFilterDateChange('fromDate', date)}
                value={filter.current.fromDate ? dayjs(filter.current.fromDate) : null}
            />

            <DatePicker
                name='toDate'
                slotProps={{
                    textField: {
                        size: 'small',
                        placeholder: 'To Date'
                    }
                }}
                sx={{
                    width: '15%',
                    display: (!searchOnly && showDates) ? 'flex' : 'none'
                }}
                onChange={(date) => handleFilterDateChange('toDate', date)}
                value={filter.current.toDate ? dayjs(filter.current.toDate) : null}
            />

            <TextField
                name='priority'
                size='small'
                select
                sx={{
                    width: '10%',
                    '& .MuiSelect-select span::before': {
                        content: "'Priority'",
                    },
                    display: !searchOnly ? 'flex' : 'none'
                }}
                onChange={handleFilterChange}
                value={filter.current.priority}
            >
                <MenuItem value=''><em>All</em></MenuItem>
                {priorities.map((p) => (
                    <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
            </TextField>

            <TextField
                name='source'
                size='small'
                select
                sx={{
                    width: '10%',
                    '& .MuiSelect-select span::before': {
                        content: "'Source'",
                    },
                    display: !searchOnly ? 'flex' : 'none'
                }}
                onChange={handleFilterChange}
                value={sourceFilter}
            >
                <MenuItem value=''><em>All</em></MenuItem>
                {sources.map((s) => (
                    <MenuItem key={s.sourceId} value={s.name}>{s.description || s.name}</MenuItem>
                ))}
            </TextField>

            <Tooltip title="Apply filter">
                <IconButton
                    onClick={handleFilter}
                    color='primary'
                    sx={{ mt: -1, ml: -1 }}
                >
                    <FilterAltIcon fontSize="large" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Clear filters">
                <IconButton
                    onClick={handleClearFilter}
                    color='primary'
                    sx={{ mt: -1, ml: -1 }}
                >
                    <FilterAltOffIcon fontSize="large" />
                </IconButton>
            </Tooltip>

        </>
    );
}

export default CustomFilter;